import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Modal, Button, Badge } from 'react-bootstrap';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
    const category = searchParams.get('category');
    if (category) {
      setActiveFilters(new Set([category]));
    }
  }, [searchParams]);

  useEffect(() => {
    filterProducts();
  }, [products, activeFilters]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
      // Filter out soft-deleted products as extra safety measure
      const activeProducts = response.data.filter(product => !product.isDeleted);
      setProducts(activeProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const filterProducts = () => {
    let filtered = products;
    
    if (activeFilters.size > 0) {
      filtered = filtered.filter(product => 
        activeFilters.has(product.category) || 
        (product.spiceLevel && activeFilters.has(product.spiceLevel))
      );
    }
    
    setFilteredProducts(filtered);
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
  const spiceLevels = ['Mild', 'Medium', 'Spicy'];
  
  const toggleFilter = (filter) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
  };

  const addToCart = async (product) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/cart`, {
        userId: 'demo-user',
        productId: product.id,
        quantity: 1,
        name: product.name,
        price: product.price
      });
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add product to cart');
    }
  };

  return (
    <div style={{ padding: '2rem 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: '2rem' }}>Our Products</h1>
        
        {/* Toggle Badge Filters */}
        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: 'var(--white)', borderRadius: '12px' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h5 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Categories:</h5>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {categories.map(category => (
                <Badge
                  key={category}
                  bg={activeFilters.has(category) ? 'primary' : 'outline-secondary'}
                  text={activeFilters.has(category) ? 'white' : 'dark'}
                  style={{ 
                    cursor: 'pointer', 
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    border: activeFilters.has(category) ? 'none' : '1px solid #6c757d'
                  }}
                  onClick={() => toggleFilter(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>
          
          <div>
            <h5 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Spice Level:</h5>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {spiceLevels.map(level => (
                <Badge
                  key={level}
                  bg={activeFilters.has(level) ? 'danger' : 'outline-secondary'}
                  text={activeFilters.has(level) ? 'white' : 'dark'}
                  style={{ 
                    cursor: 'pointer', 
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    border: activeFilters.has(level) ? 'none' : '1px solid #6c757d'
                  }}
                  onClick={() => toggleFilter(level)}
                >
                  {level}
                </Badge>
              ))}
            </div>
          </div>
          
          {activeFilters.size > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setActiveFilters(new Set())}
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-3">
          {filteredProducts.map(product => (
            <div key={product.id} className="card" style={{ backgroundColor: 'white', display: 'flex', flexDirection: 'column', height: '100%' }}>
              {product.image && (
                <img src={product.image} alt={product.name} style={{ width: '100%', height: '250px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }} />
              )}
              <h3>{product.name}</h3>
              <p style={{ color: 'var(--neutral-dark)', opacity: 0.8, marginBottom: '1rem' }}>
                {product.description ? product.description.substring(0, 50) + '...' : 'Premium quality snack'}
              </p>
              
              <div style={{ marginBottom: '1rem' }}>
                <span className="badge" style={{ backgroundColor: '#e3f2fd', color: '#1976d2', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                  {product.category}
                </span>
                {(product.weight || product.sku) && (
                  <span className="badge" style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                    {product.weight ? `${product.weight}` : `SKU: ${product.sku}`}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary-saffron)' }}>
                    S${product.price.toFixed(2)}
                  </div>
                  {product.stockStatus !== undefined && (
                    <div style={{ fontSize: '0.875rem', color: product.stockStatus ? 'var(--accent-green)' : '#f44336' }}>
                      {product.stockStatus ? 'In Stock' : 'Out of Stock'}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                <button 
                  className="btn btn-outline" 
                  style={{ flex: 1, fontSize: '0.875rem' }}
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowModal(true);
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--neutral-dark)', opacity: 0.6 }}>
            <p>No products found matching your filters.</p>
          </div>
        )}

        {/* Product Detail Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{selectedProduct?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                {selectedProduct?.image ? (
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '300px', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span>No Image Available</span>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <h4 style={{ color: 'var(--primary-saffron)', marginBottom: '1rem' }}>
                  S${selectedProduct?.price?.toFixed(2) || '0.00'}
                </h4>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Category:</strong> {selectedProduct?.category}
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '2rem' }}>
                  {selectedProduct?.description || 'Premium quality snack made with authentic ingredients and traditional methods.'}
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{ 
                    backgroundColor: selectedProduct?.stockStatus ? 'var(--accent-green)' : '#f44336',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem'
                  }}>
                    {selectedProduct?.stockStatus ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button 
              variant="primary" 
              style={{ backgroundColor: 'var(--primary-saffron)', border: 'none', opacity: 0.6 }}
              disabled
            >
              Add to Cart
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Products;